/**
 * The background-effect compositor. CLIENT-SIDE, always: the server forwards
 * the canvas capture exactly like any other camera track and never decodes a
 * pixel, which is what keeps the EX44 at ~300 concurrent participants.
 *
 * Per frame, into WORK layers whose size follows the perf tier:
 *   1. background — the frame blurred, or the chosen image, cover-fitted
 *   2. subject    — the frame masked by the segmentation stencil
 *   3. composite  — subject over background, upscaled into the published canvas
 *
 * The PUBLISHED canvas never changes size, because a mid-stream resolution
 * change forces a keyframe and re-negotiates simulcast. Only the work layers
 * shrink, which is where the pixels actually cost something.
 *
 * Cost is measured every frame and fed to `effectBudget`. When the device
 * cannot keep up the pipeline steps down and finally passes the raw frame
 * through untouched — the publication is never interrupted, only the effect.
 */
import type { VideoEffectRuntime } from '../settings/preferences';
import {
  createLayer, drawCover, publishableSize, resizeLayer, type Size,
} from './effectCanvas';
import { TIER_SPECS, initialBudget, stepBudget, type BudgetState } from './effectBudget';
import { createMaskStencil } from './maskCanvas';
import type { PersonSegmenter } from './segmenter';

export interface ResolvedEffect {
  readonly kind: 'blur' | 'image';
  readonly blurPx: number;
  readonly background: CanvasImageSource | null;
}

export interface VideoEffectPipeline {
  readonly track: MediaStreamTrack;
  setEffect(effect: ResolvedEffect): void;
  stop(): void;
}

export const OUTPUT_FPS = 24;
/** Blur bleeds transparency in from the canvas edge; overdraw hides the seam. */
const BLUR_OVERDRAW = 1.08;
const FEATHER_PX = 3;
const STATUS_INTERVAL_MS = 1_000;
/** Frames to wait for a first mask before declaring the model unusable here. */
const MASK_PATIENCE = 72;
const NO_MASK_REASON =
  'Background effect turned off — segmentation produced no usable mask on this device.';

/**
 * Starts compositing `source` and returns the track to publish. `segmenter` is
 * required: the caller only builds a pipeline once real segmentation is known
 * to be available, so this module never has to decide whether to try.
 */
export function createVideoEffectPipeline(
  source: MediaStreamTrack,
  effect: ResolvedEffect,
  segmenter: PersonSegmenter,
  onStatus: (status: VideoEffectRuntime) => void,
): VideoEffectPipeline {
  const settings = source.getSettings();
  const size = publishableSize(settings.width ?? 640, settings.height ?? 360);

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([source]);
  void video.play().catch(() => undefined);

  const out = createLayer(size, true);
  const background = createLayer(size, true);
  const subject = createLayer(size, false);
  const stencil = createMaskStencil();

  const stream = out.canvas.captureStream(OUTPUT_FPS);
  const track = stream.getVideoTracks()[0];
  if (!track) throw new Error('Background effect produced no video track.');

  let current = effect;
  let budget: BudgetState = initialBudget;
  let work: Size = size;
  let frames = 0;
  let hasMask = false;
  let maskless = 0;
  let overrideReason: string | null = null;
  let running = true;
  let timer: number | null = null;
  let lastStatusAt = 0;
  let sinceStatus = 0;

  const resizeWork = (scale: number): void => {
    const next: Size = {
      width: Math.max(2, Math.round(size.width * scale)),
      height: Math.max(2, Math.round(size.height * scale)),
    };
    if (!resizeLayer(background, next)) return;
    resizeLayer(subject, next);
    work = next;
  };

  const publishStatus = (now: number): void => {
    sinceStatus += 1;
    if (now - lastStatusAt < STATUS_INTERVAL_MS) return;
    const fps = lastStatusAt === 0 ? 0 : Math.round(sinceStatus / ((now - lastStatusAt) / 1_000));
    lastStatusAt = now;
    sinceStatus = 0;
    onStatus({
      kind: current.kind,
      active: budget.tier < 3 && hasMask,
      degraded: budget.tier > 0 || overrideReason !== null,
      reason: overrideReason ?? TIER_SPECS[budget.tier].reason,
      fps,
    });
  };

  const drawBackground = (ctx: CanvasRenderingContext2D): void => {
    if (current.kind === 'image' && current.background) {
      drawCover(ctx, current.background, work);
      return;
    }
    const w = work.width * BLUR_OVERDRAW;
    const h = work.height * BLUR_OVERDRAW;
    ctx.filter = `blur(${current.blurPx}px)`;
    ctx.drawImage(video, (work.width - w) / 2, (work.height - h) / 2, w, h);
    ctx.filter = 'none';
  };

  const drawSubject = (ctx: CanvasRenderingContext2D): void => {
    ctx.globalCompositeOperation = 'copy';
    ctx.filter = 'none';
    ctx.drawImage(video, 0, 0, work.width, work.height);
    // A hard stencil edge crawls; a few pixels of blur is the whole trick.
    ctx.globalCompositeOperation = 'destination-in';
    ctx.filter = `blur(${FEATHER_PX}px)`;
    ctx.drawImage(stencil.canvas, 0, 0, work.width, work.height);
    ctx.globalCompositeOperation = 'source-over';
    ctx.filter = 'none';
  };

  const passthrough = (ctx: CanvasRenderingContext2D, now: number): void => {
    ctx.drawImage(video, 0, 0, size.width, size.height);
    publishStatus(now);
  };

  const render = (): void => {
    const outCtx = out.ctx;
    const bgCtx = background.ctx;
    const fgCtx = subject.ctx;
    if (!running || !outCtx || !bgCtx || !fgCtx || video.readyState < 2) return;
    const started = performance.now();

    if (budget.tier === 3) {
      passthrough(outCtx, started);
      return;
    }

    const spec = TIER_SPECS[budget.tier];
    resizeWork(spec.scale);

    if (frames % spec.segmentEvery === 0) {
      const mask = segmenter.segment(video, started);
      if (mask) hasMask = stencil.write(mask, segmenter.width, segmenter.height) || hasMask;
    }
    frames += 1;

    if (!hasMask) {
      maskless += 1;
      if (maskless >= MASK_PATIENCE) {
        budget = { ...budget, tier: 3 };
        overrideReason = NO_MASK_REASON;
      }
      passthrough(outCtx, started);
      return;
    }

    drawBackground(bgCtx);
    drawSubject(fgCtx);
    bgCtx.drawImage(subject.canvas, 0, 0);
    outCtx.drawImage(background.canvas, 0, 0, size.width, size.height);

    budget = stepBudget(budget, performance.now() - started);
    publishStatus(started);
  };

  timer = window.setInterval(render, Math.round(1_000 / OUTPUT_FPS));

  return {
    track,
    setEffect(next) {
      current = next;
    },
    stop() {
      running = false;
      if (timer !== null) window.clearInterval(timer);
      timer = null;
      video.srcObject = null;
      for (const t of stream.getTracks()) t.stop();
    },
  };
}
